import * as React from 'react';
import { useDrag } from 'react-use-gesture';
import { ReactComponent as DiceArrow } from '../../assets/icons/dice-arrow-light.svg';
import { FiberDiceCanvas } from '../FiberDiceCanvas/FiberDiceCanvas';
import { DragInfo } from '../FiberDiceCanvas/diceTypes';
import { getDistance, getRotationAngle } from '../FiberDiceCanvas/utils';
import { Delay } from '../common/Delay';
import styles from './DiceBoard.module.css';
import { Stack, StackDirection } from '../common/Stack';
import { useAlterntaeArrow } from '../../redux/settings/selectors';

const SWIPE_DURATION = 200;
const ARROW_SHOW_DELAY = 150;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
};

export const DiceBoard: React.FC<Props> = ({ style, className, children }) => {
  const [dragInfo, setDragInfo] = React.useState<DragInfo | undefined>(undefined);
  const alterntaeArrow = useAlterntaeArrow();

  const bindGestures = useDrag(
    ({ tap, active, movement: [mx, my], vxvy: [vx, vy], xy, initial, swipe: [swipeX, swipeY] }) => {
      const position = xy;
      const startPosition = initial;

      if (active) {
        // darg
        setDragInfo({ eventType: 'drag', position2D: position, startPosition2D: startPosition });
      } else if (tap) {
        // tap
        setDragInfo({ eventType: 'tap', position2D: position, startPosition2D: startPosition });
      } else if (swipeX !== 0 || swipeY != 0) {
        // swipe
        setDragInfo({
          eventType: 'swipe',
          position2D: position,
          startPosition2D: startPosition,
          velocity: [vx * 30, vy * 30],
        });
      } else {
        // slingshot
        setDragInfo({
          eventType: 'slingshot',
          position2D: position,
          startPosition2D: startPosition,
          velocity: [-mx / 5, -my / 5],
        });
      }
    },
    {
      swipeDistance: 0.01,
      swipeDuration: SWIPE_DURATION,
      swipeVelocity: 0.01,
      useTouch: true,
    },
  );

  const arrowSize = dragInfo ? getDistance(dragInfo.position2D, dragInfo.startPosition2D) : 0;
  const angleRad = dragInfo ? getRotationAngle(dragInfo.startPosition2D, dragInfo.position2D) : 0;

  return (
    <div className={className} style={style}>
      <div className={styles.container}>
        <div className={styles.eventInterceptor} />
        <div className={styles.content} {...bindGestures()}>
          {children}
        </div>
        <div className={styles.board}>
          {/* <SubscriptionTest /> */}

          {dragInfo?.eventType === 'drag' && (
            <Delay delayMs={ARROW_SHOW_DELAY}>
              {alterntaeArrow
                ? getAlterntaeArrowComponent(dragInfo, arrowSize, angleRad)
                : getDefaultArrowComponent(dragInfo, arrowSize, angleRad)}
            </Delay>
          )}

          <FiberDiceCanvas dragInfo={dragInfo} onDicesRolled={() => setDragInfo(undefined)} />
        </div>
      </div>
    </div>
  );
};

const getDefaultArrowComponent = (dragInfo: DragInfo, arrowSize: number, angleRad: number): JSX.Element => (
  <DiceArrow
    style={{
      position: 'absolute',
      left: dragInfo?.startPosition2D[0],
      top: dragInfo?.startPosition2D[1],
      width: arrowSize,
      transformOrigin: '0% 0%',
      transform: `rotate(${angleRad}rad) translateY(-50%)`, //NOTE: order of transformations matters
    }}
  />
);

const getAlterntaeArrowComponent = (dragInfo: DragInfo, arrowSize: number, angleRad: number): JSX.Element => {
  const arrowCount = arrowSize / 100;
  const arrows: React.ReactElement[] = [];
  const arrowColor = `rgb(${(255 * arrowCount) / 5},${255 * (5 - arrowCount)},${0})`;

  //R = (255 * n) / 100
  //G = (255 * (100 - n)) / 100
  //B = 0

  for (let i = 0; i < arrowCount; ++i) {
    arrows.push(<RollArrow key={`arrow-${i}`} fill={arrowColor} />);
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: dragInfo?.startPosition2D[0],
        top: dragInfo?.startPosition2D[1],
        width: arrowSize,
        transformOrigin: '0% 0%',
        transform: `rotate(${angleRad}rad) translateY(-50%)`, //NOTE: order of transformations matters
      }}>
      <Stack direction={StackDirection.Horizontal} justifyContent="flex-start">
        {arrows}
      </Stack>
    </div>
  );
};

type RollArrowProps = {
  fill: string;
  className?: string;
  animationDelay?: number;
};

export const RollArrow: React.FC<RollArrowProps> = ({ fill, className = styles.arrow }) => {
  return (
    <div className={className}>
      <svg width="88.38837px" height="141.42136px" viewBox="0 0 88.38837 141.42136">
        <path
          // eslint-disable-next-line max-len
          d="M67.1752 137.886L3.53555 74.2462Q3.44876 74.1594 3.36633 74.0685Q3.2839 73.9775 3.20603 73.8827Q3.12816 73.7878 3.05505 73.6892Q2.98193 73.5906 2.91373 73.4885Q2.84554 73.3865 2.78244 73.2812Q2.71934 73.1759 2.66148 73.0677Q2.60362 72.9594 2.55114 72.8484Q2.49866 72.7375 2.45169 72.6241Q2.40471 72.5107 2.36336 72.3951Q2.32201 72.2796 2.28638 72.1621Q2.25075 72.0446 2.22093 71.9256Q2.1911 71.8065 2.16716 71.6861Q2.14321 71.5657 2.1252 71.4443Q2.10719 71.3229 2.09516 71.2008Q2.08313 71.0786 2.07711 70.956Q2.07108 70.8334 2.07108 70.7107Q2.07108 70.5879 2.07711 70.4653Q2.08313 70.3428 2.09516 70.2206Q2.10719 70.0984 2.1252 69.977Q2.14321 69.8556 2.16716 69.7352Q2.1911 69.6148 2.22093 69.4958Q2.25075 69.3767 2.28638 69.2593Q2.32201 69.1418 2.36336 69.0262Q2.40471 68.9107 2.45169 68.7973Q2.49866 68.6839 2.55114 68.5729Q2.60362 68.4619 2.66148 68.3537Q2.71934 68.2454 2.78244 68.1402Q2.84554 68.0349 2.91373 67.9328Q2.98193 67.8308 3.05505 67.7322Q3.12816 67.6336 3.20603 67.5387Q3.2839 67.4438 3.36633 67.3529Q3.44876 67.2619 3.53555 67.1751L67.1752 3.53554Q67.262 3.44875 67.3529 3.36632Q67.4439 3.28389 67.5387 3.20602Q67.6336 3.12815 67.7322 3.05503Q67.8308 2.98192 67.9329 2.91372Q68.0349 2.84553 68.1402 2.78243Q68.2455 2.71932 68.3537 2.66146Q68.462 2.6036 68.5729 2.55112Q68.6839 2.49864 68.7973 2.45167Q68.9107 2.4047 69.0263 2.36335Q69.1418 2.322 69.2593 2.28637Q69.3767 2.25074 69.4958 2.22091Q69.6149 2.19109 69.7352 2.16714Q69.8556 2.1432 69.9771 2.12519Q70.0985 2.10718 70.2206 2.09515Q70.3428 2.08312 70.4654 2.07709Q70.588 2.07107 70.7107 2.07107Q70.8335 2.07107 70.956 2.07709Q71.0787 2.08312 71.2008 2.09515Q71.3229 2.10718 71.4444 2.12519Q71.5658 2.1432 71.6861 2.16714Q71.8065 2.19109 71.9256 2.22091Q72.0447 2.25074 72.1621 2.28637Q72.2796 2.322 72.3951 2.36335Q72.5107 2.4047 72.6241 2.45167Q72.7375 2.49864 72.8485 2.55112Q72.9594 2.6036 73.0677 2.66146Q73.1759 2.71933 73.2812 2.78243Q73.3865 2.84553 73.4885 2.91372Q73.5906 2.98192 73.6892 3.05503Q73.7878 3.12815 73.8827 3.20602Q73.9775 3.28389 74.0685 3.36632Q74.1594 3.44875 74.2462 3.53554L84.8528 14.1421Q84.9396 14.2289 85.022 14.3199Q85.1045 14.4108 85.1824 14.5057Q85.2602 14.6006 85.3333 14.6992Q85.4064 14.7977 85.4746 14.8998Q85.5428 15.0019 85.6059 15.1071Q85.669 15.2124 85.7269 15.3207Q85.7848 15.4289 85.8372 15.5399Q85.8897 15.6508 85.9367 15.7642Q85.9837 15.8776 86.025 15.9932Q86.0664 16.1088 86.102 16.2262Q86.1376 16.3437 86.1674 16.4628Q86.1973 16.5818 86.2212 16.7022Q86.2452 16.8226 86.2632 16.944Q86.2812 17.0654 86.2932 17.1876Q86.3052 17.3097 86.3113 17.4323Q86.3173 17.5549 86.3173 17.6777Q86.3173 17.8004 86.3113 17.923Q86.3052 18.0456 86.2932 18.1677Q86.2812 18.2899 86.2632 18.4113Q86.2452 18.5327 86.2212 18.6531Q86.1973 18.7735 86.1674 18.8926Q86.1376 19.0116 86.102 19.1291Q86.0664 19.2465 86.025 19.3621Q85.9837 19.4777 85.9367 19.5911Q85.8897 19.7045 85.8372 19.8154Q85.7848 19.9264 85.7269 20.0346Q85.669 20.1429 85.6059 20.2482Q85.5428 20.3534 85.4746 20.4555Q85.4064 20.5576 85.3333 20.6562Q85.2602 20.7547 85.1824 20.8496Q85.1045 20.9445 85.0221 21.0354Q84.9396 21.1264 84.8528 21.2132L38.8909 67.1751Q38.8041 67.2619 38.7217 67.3529Q38.6392 67.4438 38.5614 67.5387Q38.4835 67.6336 38.4104 67.7322Q38.3373 67.8308 38.2691 67.9328Q38.2009 68.0349 38.1378 68.1402Q38.0747 68.2454 38.0168 68.3537Q37.959 68.4619 37.9065 68.5729Q37.854 68.6839 37.807 68.7972Q37.7601 68.9107 37.7187 69.0262Q37.6774 69.1418 37.6417 69.2592Q37.6061 69.3767 37.5763 69.4958Q37.5464 69.6148 37.5225 69.7352Q37.4986 69.8556 37.4805 69.977Q37.4625 70.0984 37.4505 70.2206Q37.4385 70.3427 37.4324 70.4653Q37.4264 70.5879 37.4264 70.7107Q37.4264 70.8334 37.4325 70.956Q37.4385 71.0786 37.4505 71.2008Q37.4625 71.3229 37.4805 71.4443Q37.4986 71.5657 37.5225 71.6861Q37.5464 71.8065 37.5763 71.9256Q37.6061 72.0446 37.6417 72.1621Q37.6774 72.2796 37.7187 72.3951Q37.7601 72.5107 37.807 72.6241Q37.854 72.7375 37.9065 72.8484Q37.959 72.9594 38.0168 73.0677Q38.0747 73.1759 38.1378 73.2812Q38.2009 73.3865 38.2691 73.4885Q38.3373 73.5906 38.4104 73.6892Q38.4835 73.7878 38.5614 73.8826Q38.6392 73.9775 38.7217 74.0685Q38.8041 74.1594 38.8909 74.2462L84.8528 120.208Q84.9396 120.295 85.022 120.386Q85.1045 120.477 85.1824 120.572Q85.2602 120.667 85.3333 120.765Q85.4064 120.864 85.4746 120.966Q85.5428 121.068 85.6059 121.173Q85.669 121.278 85.7269 121.387Q85.7848 121.495 85.8372 121.606Q85.8897 121.717 85.9367 121.83Q85.9837 121.944 86.025 122.059Q86.0664 122.175 86.102 122.292Q86.1376 122.41 86.1674 122.529Q86.1973 122.648 86.2212 122.768Q86.2452 122.889 86.2632 123.01Q86.2812 123.131 86.2932 123.254Q86.3053 123.376 86.3113 123.498Q86.3173 123.621 86.3173 123.744Q86.3173 123.866 86.3113 123.989Q86.3053 124.112 86.2932 124.234Q86.2812 124.356 86.2632 124.477Q86.2452 124.599 86.2212 124.719Q86.1973 124.84 86.1674 124.959Q86.1376 125.078 86.102 125.195Q86.0664 125.313 86.025 125.428Q85.9837 125.544 85.9367 125.657Q85.8897 125.771 85.8372 125.881Q85.7848 125.992 85.7269 126.101Q85.669 126.209 85.6059 126.314Q85.5428 126.419 85.4746 126.522Q85.4065 126.624 85.3333 126.722Q85.2602 126.821 85.1824 126.916Q85.1045 127.011 85.0221 127.101Q84.9396 127.192 84.8528 127.279L74.2462 137.886Q74.1594 137.973 74.0685 138.055Q73.9776 138.137 73.8827 138.215Q73.7878 138.293 73.6892 138.366Q73.5906 138.439 73.4886 138.508Q73.3865 138.576 73.2812 138.639Q73.1759 138.702 73.0677 138.76Q72.9594 138.818 72.8485 138.87Q72.7375 138.923 72.6241 138.97Q72.5107 139.017 72.3952 139.058Q72.2796 139.099 72.1621 139.135Q72.0447 139.171 71.9256 139.2Q71.8065 139.23 71.6861 139.254Q71.5658 139.278 71.4444 139.296Q71.3229 139.314 71.2008 139.326Q71.0786 139.338 70.956 139.344Q70.8335 139.35 70.7107 139.35Q70.588 139.35 70.4654 139.344Q70.3428 139.338 70.2206 139.326Q70.0985 139.314 69.9771 139.296Q69.8556 139.278 69.7353 139.254Q69.6149 139.23 69.4958 139.2Q69.3767 139.171 69.2593 139.135Q69.1418 139.099 69.0263 139.058Q68.9107 139.017 68.7973 138.97Q68.6839 138.923 68.5729 138.87Q68.462 138.818 68.3537 138.76Q68.2455 138.702 68.1402 138.639Q68.0349 138.576 67.9329 138.508Q67.8308 138.439 67.7322 138.366Q67.6336 138.293 67.5387 138.215Q67.4439 138.137 67.3529 138.055Q67.262 137.973 67.1752 137.886Z"
          id="arrow"
          fill={fill}
          fillRule="evenodd"
          stroke="none"
          // className={className}
          // style={{ animationDelay: `${animationDelay}ms` }}
        />
      </svg>
    </div>
  );
};
